import { SubmitEvent, useCallback, useEffect, useState } from "react"
import { Text, Button, Input } from "../../../components"
import { CrmRequest, ensureOk, getErrorMessage } from "../api"
import { ListStatus } from "../components"

type LogLevel = 'INFO' | 'WARNING' | 'ERROR'

interface LogEntry {
    time: string;
    level: LogLevel | string;
    source: string;
    message: string;
    ip?: string | null;
    traceback?: string;
}

const LEVEL_FILTERS: {key: LogLevel; label: string}[] = [
    {key: 'INFO', label: 'Все'},
    {key: 'WARNING', label: 'Предупреждения'},
    {key: 'ERROR', label: 'Ошибки'},
]

const LOGS_LIMIT = 300
const AUTO_REFRESH_MS = 5000

// Для логов важны секунды, поэтому свой формат, а не formatDateTime
const formatLogTime = (iso: string) => {
    const date = new Date(iso)
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('ru-RU', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'})
}

const getLevelClass = (level: string) => {
    if (level === 'ERROR' || level === 'CRITICAL') return 'crm-log-error'
    if (level === 'WARNING') return 'crm-log-warning'
    return 'crm-log-info'
}

export const LogsTab = ({request}: {request: CrmRequest}) => {
    const [level, setLevel] = useState<LogLevel>('INFO')
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [autoRefresh, setAutoRefresh] = useState(false)
    const [logs, setLogs] = useState<LogEntry[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true)
        setError(null)
        try {
            const params = new URLSearchParams({limit: String(LOGS_LIMIT), level})
            if (search) params.set('search', search)
            const res = await request(`/crm/get-logs?${params}`)
            if (!res) return
            await ensureOk(res, 'Не удалось загрузить логи')
            const data = await res.json()
            setLogs(data.logs ?? [])
        } catch (err) {
            setError(getErrorMessage(err, 'Не удалось загрузить логи'))
        } finally {
            setLoading(false)
        }
    }, [request, level, search])

    useEffect(() => {
        load()
    }, [load])

    useEffect(() => {
        if (!autoRefresh) return
        const timer = setInterval(() => load(true), AUTO_REFRESH_MS)
        return () => clearInterval(timer)
    }, [autoRefresh, load])

    const submitSearch = (e: SubmitEvent<HTMLFormElement>) => {
        e.preventDefault()
        setSearch(searchInput.trim())
    }

    return (
        <div className="crm-list">
            <div className="admin-login-tabs">
                {LEVEL_FILTERS.map(({key, label}) => (
                    <div key={key} className={`admin-login-tab${level === key ? ' admin-login-tab-active' : ''}`}>
                        <Button variant="ghost" fullWidth textSize="s" textColor={level === key ? 'white' : 'lightGray'} onClick={() => setLevel(key)}>{label}</Button>
                    </div>
                ))}
            </div>

            <form className="crm-logs-search" onSubmit={submitSearch}>
                <Input
                    label="Поиск по пути, IP, тексту ошибки"
                    autoComplete="off"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
                <Button type="submit" variant="ghost" textSize="s" textColor="lightGray">Найти</Button>
            </form>

            <div className="crm-logs-toolbar">
                <label className="crm-logs-auto">
                    <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                    <Text size="xs" color="lightGray">Обновлять каждые 5 с</Text>
                </label>
                <button type="button" className="admin-edit-btn" disabled={loading} onClick={() => load()}>
                    <Text size="xs" color="lightGray">Обновить</Text>
                </button>
            </div>

            <ListStatus loading={loading} error={error} empty={logs.length === 0} emptyText="Записей нет" />

            {!loading && logs.length > 0 && (
                <div className="crm-logs">
                    {logs.map((entry, index) => (
                        <div key={`${entry.time}-${index}`} className={`crm-log ${getLevelClass(entry.level)}`}>
                            <div className="crm-log-head">
                                <span className="crm-log-level">{entry.level}</span>
                                <span className="crm-log-time">{formatLogTime(entry.time)}</span>
                                {entry.ip && <span className="crm-log-time">{entry.ip}</span>}
                            </div>
                            <span className="crm-log-message">{entry.message}</span>
                            {entry.traceback && (
                                <details className="crm-log-traceback">
                                    <summary>Трейсбек</summary>
                                    <pre>{entry.traceback}</pre>
                                </details>
                            )}
                        </div>
                    ))}
                    {logs.length === LOGS_LIMIT && (
                        <Text size="xs" color="lightGray">{`Показаны последние ${LOGS_LIMIT} записей — уточните поиск, чтобы найти более старые`}</Text>
                    )}
                </div>
            )}
        </div>
    )
}
